import groupBy from 'lodash/groupBy';
import React, { useMemo, useState } from 'react';

import { QUERY_ROOT_ID, useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Stack,
  Text,
} from '@eluve/components';
import { DbEnums } from '@eluve/graphql-types';

import {
  modelPromptTemplatesFragment,
  outputTemplatesFragment,
  promptTemplatesFragment,
} from './eval.operations';

export type TargetConfig = {
  modelPromptTemplateId: string;
  outputTemplateId: string | null;
};

export interface FactVerificationTargetConfigsFormProps {
  selectedConfigs: TargetConfig[];
  onSelectionChanged: (selectedConfigs: TargetConfig[]) => void;
}

export const FactVerificationTargetConfigsForm: React.FC<
  FactVerificationTargetConfigsFormProps
> = ({ onSelectionChanged, selectedConfigs }) => {
  const { promptTemplates } = useCompleteFragment({
    fragment: promptTemplatesFragment,
    key: QUERY_ROOT_ID,
  });

  const { llmModelPromptTemplates: modelPromptTemplates } = useCompleteFragment(
    {
      fragment: modelPromptTemplatesFragment,
      key: QUERY_ROOT_ID,
    },
  );

  const { llmOutputTemplates } = useCompleteFragment({
    fragment: outputTemplatesFragment,
    key: QUERY_ROOT_ID,
  });

  // State for selected items
  const [selectedPromptTemplateId, setSelectedPromptTemplateId] = useState<
    string | null
  >(null);
  const [selectedModelPromptTemplateId, setSelectedModelPromptTemplateId] =
    useState<string | null>(null);
  const [selectedOutputTemplateId, setSelectedOutputTemplateId] = useState<
    string | null
  >(null);

  const groupedByPrompt = groupBy(
    modelPromptTemplates,
    (mpt) => mpt.template.id,
  );

  // Filter model-prompt templates based on selected prompt template
  const modelPromptTemplateOptions = useMemo(() => {
    if (!selectedPromptTemplateId) return [];

    return groupedByPrompt[selectedPromptTemplateId];
  }, [selectedPromptTemplateId, groupedByPrompt]);

  const selectedPromptTemplate = modelPromptTemplates.find(
    (mpt) => mpt.template.id === selectedPromptTemplateId,
  )?.template;

  const selectedModelArgs = modelPromptTemplates.find(
    (mpt) => mpt.id === selectedModelPromptTemplateId,
  )?.model_args;
  const selectedOutputTemplate = llmOutputTemplates.find(
    (ot) => ot.id === selectedOutputTemplateId,
  );

  const isSelectedDynamicOutput =
    selectedPromptTemplate?.outputType === DbEnums.LlmOutputType.DYNAMIC_OUTPUT;

  const configAlreadyExists = selectedConfigs.some(
    (config) =>
      config.modelPromptTemplateId === selectedModelPromptTemplateId &&
      config.outputTemplateId === selectedOutputTemplateId,
  );

  const isValidConfig = Boolean(
    selectedPromptTemplateId &&
    selectedPromptTemplate &&
    selectedModelArgs &&
    selectedModelPromptTemplateId &&
    (!isSelectedDynamicOutput || selectedOutputTemplateId) &&
    !configAlreadyExists,
  );

  return (
    <>
      {/* Selected Configs */}
      <Stack gap={4}>
        {selectedConfigs.map((config) => {
          const mpt = modelPromptTemplates.find(
            (mpt) => mpt.id === config.modelPromptTemplateId,
          );

          const pt = mpt?.template;
          const ma = mpt?.model_args;
          const ot = llmOutputTemplates.find(
            (ot) => ot.id === config.outputTemplateId,
          );

          if (!pt || !ma) return null;

          return (
            <Stack
              key={`${config.modelPromptTemplateId}-${ot?.id ?? ''}`}
              gap={4}
              className="rounded-lg bg-border-primary p-4"
            >
              <Stack align="end" gap={2} direction="horizontal">
                <Text weight="semibold" className="text-lg">
                  {pt.name}
                </Text>
              </Stack>

              <Stack gap={5} key={pt.id} align="start" direction="horizontal">
                <Stack gap={2}>
                  <Text>{ma.name}</Text>
                  <Text>{JSON.stringify(ma.args)}</Text>
                </Stack>
                {pt.outputType === DbEnums.LlmOutputType.DYNAMIC_OUTPUT && (
                  <Stack className="max-h-[600px] overflow-y-auto" gap={2}>
                    <Text>Output Template</Text>
                    <Text>{ot?.name}</Text>
                  </Stack>
                )}
                <Button
                  onClick={() => {
                    onSelectionChanged(
                      selectedConfigs.filter(
                        (c) =>
                          !(
                            c.modelPromptTemplateId ===
                              config.modelPromptTemplateId &&
                            c.outputTemplateId === ot?.id
                          ),
                      ),
                    );
                  }}
                >
                  Remove
                </Button>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      {/* Selection dropdowns */}
      <Stack gap={4}>
        {/* PROMPT TEMPLATE */}
        <Stack gap={2}>
          <Text>Prompt Template</Text>
          <Combobox>
            <ComboboxSelectButton className="w-full justify-between">
              {selectedPromptTemplate
                ? `${selectedPromptTemplate.name} (${selectedPromptTemplate.outputType})`
                : 'Select a prompt template'}
            </ComboboxSelectButton>
            <ComboboxDropdown searchPlaceholder="Search prompt templates...">
              <ComboboxEmpty>No prompt templates found.</ComboboxEmpty>
              {promptTemplates.map((pt) => (
                <ComboboxOption
                  key={pt.id}
                  searchValue={`${pt.name} (${pt.outputType})`}
                  onSelect={() => {
                    // Reset states when a new prompt template is chosen
                    setSelectedPromptTemplateId(pt.id);
                    setSelectedModelPromptTemplateId(null);
                    setSelectedOutputTemplateId(null);
                  }}
                >
                  <Stack gap={3} direction="horizontal">
                    <ComboboxSelectCheck
                      selected={pt.id === selectedPromptTemplateId}
                    />
                    <Stack gap={5} direction="horizontal">
                      {pt.outputType}{' '}
                      <span className="font-bold">{pt.name}</span>
                    </Stack>
                  </Stack>
                </ComboboxOption>
              ))}
            </ComboboxDropdown>
          </Combobox>
        </Stack>

        {/* MODEL ARGS (only show if a prompt template is selected) */}
        {selectedPromptTemplateId && (
          <Stack gap={2}>
            <Text>Model Args</Text>
            <Combobox>
              <ComboboxSelectButton className="w-full justify-between">
                {selectedModelArgs?.name ?? 'Select model args'}
              </ComboboxSelectButton>
              <ComboboxDropdown searchPlaceholder="Search model args...">
                <ComboboxEmpty>No model args found.</ComboboxEmpty>
                {(modelPromptTemplateOptions ?? []).map((mpt) => (
                  <ComboboxOption
                    key={mpt.id}
                    searchValue={mpt.model_args.name ?? ''}
                    onSelect={() => {
                      setSelectedModelPromptTemplateId(mpt.id);
                    }}
                  >
                    <Stack gap={3} direction="horizontal">
                      <ComboboxSelectCheck
                        selected={mpt.id === selectedModelPromptTemplateId}
                      />
                      <Stack align="start" gap={2}>
                        <Stack gap={5} direction="horizontal">
                          {mpt.model_args.modelType}
                          <span className="font-bold">
                            {mpt.model_args.name}
                          </span>
                        </Stack>
                        <div>{JSON.stringify(mpt.model_args.args)}</div>
                      </Stack>
                    </Stack>
                  </ComboboxOption>
                ))}
              </ComboboxDropdown>
            </Combobox>
          </Stack>
        )}

        {/* OUTPUT TEMPLATE (only show if selectedPromptTemplate is DYNAMIC_OUTPUT) */}
        {selectedPromptTemplateId && isSelectedDynamicOutput && (
          <Stack gap={2}>
            <Text>Output Template</Text>
            <Combobox>
              <ComboboxSelectButton className="w-full justify-between">
                {selectedOutputTemplate
                  ? `${selectedOutputTemplate.name} (${selectedOutputTemplate.id})`
                  : 'Select output template'}
              </ComboboxSelectButton>
              <ComboboxDropdown searchPlaceholder="Search output templates...">
                <ComboboxEmpty>No output templates found.</ComboboxEmpty>
                {llmOutputTemplates.map((ot) => (
                  <ComboboxOption
                    key={ot.id}
                    searchValue={`${ot.name} (${ot.id})`}
                    onSelect={() => {
                      setSelectedOutputTemplateId(ot.id);
                    }}
                  >
                    <Stack gap={3} direction="horizontal">
                      <ComboboxSelectCheck
                        selected={ot.id === selectedOutputTemplateId}
                      />
                      <Stack gap={2} direction="horizontal">
                        <span className="font-bold">{ot.name}</span> ({ot.id})
                      </Stack>
                    </Stack>
                  </ComboboxOption>
                ))}
              </ComboboxDropdown>
            </Combobox>
          </Stack>
        )}

        <Button
          disabled={!isValidConfig}
          onClick={() => {
            if (!selectedPromptTemplate || !selectedModelArgs) return;

            onSelectionChanged([
              ...selectedConfigs,
              {
                modelPromptTemplateId: selectedModelPromptTemplateId!,
                outputTemplateId: selectedOutputTemplate?.id ?? null,
              },
            ]);
          }}
        >
          Use this config
        </Button>
      </Stack>
    </>
  );
};
